import { countConditions } from "@estelle/domain";
import type {
  Id,
  ListQuery,
  Page,
  Project,
  Task,
  RecurringTask,
  TaskAttachment,
  TaskComment,
  TaskLinkTarget,
  TasksRepository,
  TaskWithDetails,
  UserSummary,
  WriteResult,
} from "@estelle/domain";

import { API_BASE, apiFetch } from "@/adapters/api/base";

/**
 * Tasks, over HTTP (M12-5 / #160, also closes #127).
 *
 * The API returns the domain shapes as `apps/api/src/routes/tasks.ts`
 * documents them; nothing is remapped here — the same "no fixture fallback"
 * shape `adapters/api/contacts.ts` and `adapters/api/docs.ts` use, because
 * Tasks crosses no Shopify boundary either (`packages/domain`'s
 * `TasksRepository` comment on `create`/`update`).
 */

const BASE = "/api/tasks";

function listParams(query: ListQuery | undefined): URLSearchParams {
  const params = new URLSearchParams();
  if (!query) return params;
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.cursor !== undefined) params.set("cursor", query.cursor);
  if (query.search !== undefined && query.search !== "") {
    params.set("search", query.search);
  }
  if (query.filter && countConditions(query.filter) > 0) {
    params.set("filter", JSON.stringify(query.filter));
  }
  if (query.sort && query.sort.length > 0) {
    params.set(
      "sort",
      query.sort.map((entry) => `${entry.column}:${entry.direction}`).join(","),
    );
  }
  return params;
}

async function get<T>(path: string, params?: URLSearchParams): Promise<T> {
  const query = params?.toString() ?? "";
  const response = await apiFetch(`${BASE}${path}${query ? `?${query}` : ""}`);
  if (!response.ok) {
    throw new Error(
      `Tasks request failed: ${String(response.status)} on ${path || "/"}`,
    );
  }
  return (await response.json()) as T;
}

/**
 * A write's outcome. A refusal arrives as a `WriteResult` body with a 4xx
 * status — a rejection to show, not a failure to throw. Mirrors
 * `adapters/api/contacts.ts`'s `write`.
 */
async function write<T>(
  path: string,
  method: "POST" | "PATCH" | "DELETE",
  body?: unknown,
): Promise<WriteResult<T>> {
  const response = await apiFetch(`${BASE}${path}`, {
    method,
    headers:
      body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result = (await response
    .json()
    .catch(() => null)) as WriteResult<T> | null;
  if (result !== null && typeof result === "object" && "ok" in result) {
    return result;
  }
  throw new Error(
    `Tasks ${method} failed: ${String(response.status)} on ${path || "/"}`,
  );
}

export function createApiTasks(): TasksRepository {
  return {
    // Ids and names of the active people, for anyone who may view tasks — not
    // `/api/settings/users`, which needs `settings.users.manage` (E6, #446).
    listUsers: () => get<Page<UserSummary>>("/users"),

    listProjects: (query) => get<Page<Project>>("/projects", listParams(query)),

    createProject: (input) => write<Project>("/projects", "POST", input),

    updateProject: (id, patch) =>
      write<Project>(`/projects/${encodeURIComponent(id)}`, "PATCH", patch),

    archiveProject: (id) =>
      write<Project>(`/projects/${encodeURIComponent(id)}/archive`, "POST"),

    list: (query) => get<Page<Task>>("", listParams(query)),

    get: async (id: Id): Promise<TaskWithDetails | null> => {
      const response = await apiFetch(`${BASE}/${encodeURIComponent(id)}`);
      if (!response.ok) {
        throw new Error(`Tasks request failed: ${String(response.status)}`);
      }
      return (await response.json()) as TaskWithDetails | null;
    },

    create: (input) => write<Task>("", "POST", input),

    update: (id, patch, options) =>
      write<Task>(`/${encodeURIComponent(id)}`, "PATCH", {
        ...patch,
        overrideReason: options?.overrideReason,
      }),

    delete: async (id) => {
      const result = await write<null>(`/${encodeURIComponent(id)}`, "DELETE");
      return result.ok ? { ok: true, data: undefined } : result;
    },

    comment: (id, body) =>
      write<TaskComment>(`/${encodeURIComponent(id)}/comments`, "POST", {
        body,
      }),

    editComment: (id, commentId, body) =>
      write<TaskComment>(
        `/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
        "PATCH",
        { body },
      ),

    link: async (id, targetType, targetId) => {
      const result = await write<null>(
        `/${encodeURIComponent(id)}/links`,
        "POST",
        {
          targetType,
          targetId,
        },
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    unlink: async (id, linkId) => {
      const result = await write<null>(
        `/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    listForTarget: (targetType: TaskLinkTarget, targetId: Id) =>
      get<Page<Task>>(
        `/targets/${encodeURIComponent(targetType)}/${encodeURIComponent(targetId)}`,
      ),

    uploadAttachment: async (id, file) => {
      const body = new FormData();
      body.append(
        "file",
        new Blob([file.data.slice()], { type: file.contentType }),
        file.filename,
      );
      const response = await apiFetch(
        `${BASE}/${encodeURIComponent(id)}/attachments`,
        { method: "POST", body },
      );
      const result = (await response
        .json()
        .catch(() => null)) as WriteResult<TaskAttachment> | null;
      if (result !== null && typeof result === "object" && "ok" in result) {
        return result;
      }
      throw new Error(
        `Tasks POST failed: ${String(response.status)} on /${id}/attachments`,
      );
    },

    // The app's download route, never the bucket: the bucket is private and
    // every fetch of this URL is permission-checked on the way through.
    // Absolute, because the API is a different origin from the SPA.
    attachmentUrl: (id, attachmentId) =>
      `${API_BASE}${BASE}/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,

    // Recurring tasks (#624). A write answers a `WriteResult` as the others do.
    listRecurring: () => get<RecurringTask[]>("/recurring"),

    createRecurring: (input) =>
      write<RecurringTask>("/recurring", "POST", input),

    updateRecurring: (id, patch) =>
      write<RecurringTask>(
        `/recurring/${encodeURIComponent(id)}`,
        "PATCH",
        patch,
      ),

    deleteRecurring: async (id) => {
      const result = await write<null>(
        `/recurring/${encodeURIComponent(id)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },

    deleteAttachment: async (id, attachmentId) => {
      const result = await write<null>(
        `/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
        "DELETE",
      );
      return result.ok ? { ok: true, data: undefined } : result;
    },
  };
}
